/**
 * FormSnapshotter - Utility for capturing and comparing form state
 *
 * Captures the current state of all form inputs using the FormData API
 * and provides a method to check if any values have changed.
 *
 * Features:
 * - Uses native FormData API (includes inputs with form="form-id" attribute)
 * - Excludes inputs with data-snapshot-ignore attribute
 * - Supports all input types: text, checkbox, radio, select, file, textarea
 * - Handles multi-value inputs (checkboxes with same name, multi-select)
 *
 * Usage:
 *   const snapshot = FormSnapshotter.capture(formElement)
 *   const hasChanges = FormSnapshotter.hasChanged(formElement, snapshot)
 */

import isEqual from "lodash.isequal"

// Global registry for storing snapshots by form ID
const registry = new Map()

const FormSnapshotter = {
  /**
   * Capture a snapshot of all form input values using FormData API
   * @param {HTMLFormElement} formElement - The form element
   * @returns {Object} Snapshot object with data, fieldNames, and ignoredFields
   */
  capture(formElement) {
    const ignoredFields = this._getIgnoredFields(formElement)
    const { data, fieldNames } = this._buildDataMap(formElement, ignoredFields)
    return { data, fieldNames, ignoredFields }
  },

  /**
   * Check if any form inputs have changed since the snapshot
   * @param {HTMLFormElement} formElement - The form element
   * @param {Object} snapshot - Previously captured snapshot
   * @returns {boolean} True if any non-ignored values have changed
   */
  hasChanged(formElement, snapshot) {
    const { data, fieldNames } = this._buildDataMap(
      formElement,
      snapshot.ignoredFields,
    )

    // Check if the set of field names changed
    if (!isEqual(snapshot.fieldNames, fieldNames)) {
      return true
    }

    // Check if any values changed
    for (const name of snapshot.fieldNames) {
      if (!isEqual(snapshot.data.get(name), data.get(name))) {
        return true
      }
    }

    return false
  },

  /**
   * Store a snapshot in the registry using the form's ID
   * @param {HTMLFormElement} formElement - The form element (must have an id)
   * @returns {Object} The stored snapshot
   */
  store(formElement) {
    if (!formElement.id) {
      throw new Error("Form element must have an id to store snapshot")
    }
    const snapshot = this.capture(formElement)
    registry.set(formElement.id, snapshot)
    return snapshot
  },

  /**
   * Store a snapshot in the registry only if one doesn't already exist
   * @param {HTMLFormElement} formElement - The form element (must have an id)
   * @returns {Object} The stored snapshot (existing or newly created)
   */
  store_if_needed(formElement) {
    const existing = this.get(formElement)
    if (existing) {
      return existing
    }
    return this.store(formElement)
  },

  /**
   * Get a snapshot from the registry
   * @param {HTMLFormElement} formElement - The form element
   * @returns {Object|undefined} The snapshot, or undefined if not found
   */
  get(formElement) {
    return registry.get(formElement.id)
  },

  /**
   * Remove a snapshot from the registry
   * @param {HTMLFormElement} formElement - The form element
   */
  destroy(formElement) {
    registry.delete(formElement.id)
  },

  /**
   * Check if form has changed from stored snapshot
   * @param {HTMLFormElement} formElement - The form element
   * @returns {boolean} True if changed
   */
  changed(formElement) {
    const snapshot = this.get(formElement)
    return snapshot && this.hasChanged(formElement, snapshot)
  },

  /**
   * Build data map and field names from FormData
   * @private
   */
  _buildDataMap(formElement, ignoredFields) {
    const formData = new FormData(formElement)
    const data = new Map()
    const allFieldNames = new Set()

    for (const [name, value] of formData.entries()) {
      if (data.has(name)) {
        const existing = data.get(name)
        data.set(
          name,
          Array.isArray(existing) ? [...existing, value] : [existing, value],
        )
      } else {
        data.set(name, value)
      }
      allFieldNames.add(name)
    }

    const fieldNames = allFieldNames.difference(ignoredFields)
    return { data, fieldNames }
  },

  /**
   * Get set of field names that have data-snapshot-ignore attribute
   * @private
   */
  _getIgnoredFields(formElement) {
    const doc = formElement.ownerDocument || document
    const formId = formElement.id
    const ignoredFields = new Set()

    const inputs = [
      ...formElement.querySelectorAll("[data-snapshot-ignore]"),
      ...(formId
        ? doc.querySelectorAll(`[data-snapshot-ignore][form="${formId}"]`)
        : []),
    ]

    for (const input of inputs) {
      if (input.name) ignoredFields.add(input.name)
    }

    return ignoredFields
  },
}

export default FormSnapshotter
